import { useState, useRef } from 'react';
import axios from 'axios';
import {
  X, MapPin, Phone, Package, MessageCircle,
  CheckCircle, Truck, AlertTriangle, ChevronRight,
  Clock, Map, Camera, ImageIcon, XCircle, Plus,
  DollarSign, ChevronDown, Navigation,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

const MAX_IMAGES   = 3;
const MAX_WIDTH_PX = 1200;
const JPEG_QUALITY = 0.70;

const STATUS_CONFIG = {
  pending:    { label: 'Pendiente',   color: 'bg-yellow-100 text-yellow-800' },
  en_recojo:  { label: 'En recojo',   color: 'bg-orange-100 text-orange-800' },
  encamino:   { label: 'En camino',   color: 'bg-blue-100 text-blue-800' },
  entregado:  { label: 'Entregado',   color: 'bg-green-100 text-green-800' },
  rechazado:  { label: 'Rechazado',   color: 'bg-red-100 text-red-800' },
  cancelado:  { label: 'Cancelado',   color: 'bg-gray-100 text-gray-500' },
  postergado: { label: 'Postergado',  color: 'bg-purple-100 text-purple-800' },
};

const STATUS_LABELS_LOG = {
  pending: 'Pendiente', en_recojo: 'En recojo', encamino: 'En camino',
  entregado: 'Entregado', rechazado: 'Rechazado', cancelado: 'Cancelado', postergado: 'Postergado',
};

const ACTIVE_STATUSES = ['pending', 'en_recojo', 'encamino', 'postergado'];

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-PE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ── Image compression ──────────────────────────────────────
function compressImage(file) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > MAX_WIDTH_PX) { height = Math.round((height * MAX_WIDTH_PX) / width); width = MAX_WIDTH_PX; }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        blob => resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' })),
        'image/jpeg', JPEG_QUALITY
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

// ── Source picker sheet ────────────────────────────────────
function SourcePickerSheet({ onChoose, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-t-3xl p-4 space-y-2 pb-8">
        <div className="flex justify-center mb-2"><div className="w-10 h-1 bg-gray-200 rounded-full" /></div>
        <p className="text-xs text-center text-gray-400 font-medium mb-3">Agregar foto</p>
        <button onClick={() => onChoose('camera')} className="w-full flex items-center gap-3 bg-gray-50 hover:bg-gray-100 active:scale-95 transition-all rounded-2xl px-4 py-4">
          <div className="w-10 h-10 bg-brand-primary/10 rounded-xl flex items-center justify-center shrink-0"><Camera size={18} className="text-brand-primary" /></div>
          <div className="text-left"><div className="text-sm font-semibold text-gray-800">Cámara</div><div className="text-xs text-gray-400">Tomar una foto ahora</div></div>
        </button>
        <button onClick={() => onChoose('gallery')} className="w-full flex items-center gap-3 bg-gray-50 hover:bg-gray-100 active:scale-95 transition-all rounded-2xl px-4 py-4">
          <div className="w-10 h-10 bg-brand-primary/10 rounded-xl flex items-center justify-center shrink-0"><ImageIcon size={18} className="text-brand-primary" /></div>
          <div className="text-left"><div className="text-sm font-semibold text-gray-800">Galería</div><div className="text-xs text-gray-400">Seleccionar desde el dispositivo</div></div>
        </button>
        <button onClick={onClose} className="w-full py-3.5 rounded-2xl text-sm font-semibold text-gray-500 bg-gray-100 mt-1">Cancelar</button>
      </div>
    </div>
  );
}

// ── Shared image picker section ────────────────────────────
function ImagePickerSection({ images, setImages, compressing, setCompressing }) {
  const [showPicker, setShowPicker] = useState(false);
  const cameraRef  = useRef(null);
  const galleryRef = useRef(null);

  function handleSourceChosen(source) {
    setShowPicker(false);
    if (source === 'camera') cameraRef.current?.click();
    else galleryRef.current?.click();
  }

  async function handleFiles(e) {
    const raw    = Array.from(e.target.files || []);
    if (!raw.length) return;
    const slots  = MAX_IMAGES - images.length;
    const picked = raw.slice(0, slots);
    setCompressing(true);
    try {
      const compressed = await Promise.all(picked.map(compressImage));
      setImages(prev => [...prev, ...compressed.map(f => ({ file: f, preview: URL.createObjectURL(f) }))]);
    } finally {
      setCompressing(false);
      e.target.value = '';
    }
  }

  function removeImage(idx) {
    setImages(prev => { URL.revokeObjectURL(prev[idx].preview); return prev.filter((_, i) => i !== idx); });
  }

  return (
    <>
      {showPicker && <SourcePickerSheet onChoose={handleSourceChosen} onClose={() => setShowPicker(false)} />}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs text-gray-500 font-medium">Fotos de prueba <span className="text-red-400">*</span></label>
          <span className="text-xs text-gray-400">{images.length}/{MAX_IMAGES}</span>
        </div>
        <div className="flex gap-2 flex-wrap">
          {images.map((img, idx) => (
            <div key={idx} className="relative w-20 h-20 shrink-0">
              <img src={img.preview} alt={`foto ${idx + 1}`} className="w-full h-full object-cover rounded-xl border border-gray-200" />
              <button onClick={() => removeImage(idx)} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 shadow"><XCircle size={16} /></button>
            </div>
          ))}
          {images.length < MAX_IMAGES && (
            <button onClick={() => setShowPicker(true)} disabled={compressing} className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-1 text-gray-400 active:bg-gray-100 disabled:opacity-50 shrink-0">
              {compressing ? <div className="w-5 h-5 border-2 border-gray-300 border-t-brand-primary rounded-full animate-spin" /> : <><Plus size={20} className="text-gray-300" /><span className="text-xs">Agregar</span></>}
            </button>
          )}
        </div>
        {compressing && <p className="text-xs text-gray-400 animate-pulse">Procesando imágenes...</p>}
      </div>
      <input ref={cameraRef}  type="file" accept="image/*" capture="environment" onChange={handleFiles} className="hidden" />
      <input ref={galleryRef} type="file" accept="image/*" multiple            onChange={handleFiles} className="hidden" />
    </>
  );
}

// ── Navigation action sheet for address taps ───────────────
function AddressNavSheet({ lat, lng, label, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-t-3xl p-4 space-y-2 pb-8">
        <div className="flex justify-center mb-2"><div className="w-10 h-1 bg-gray-200 rounded-full" /></div>
        <p className="text-xs text-center text-gray-400 font-medium mb-1">
          Navegar a: <span className="text-gray-700 font-semibold">{label}</span>
        </p>
        <button onClick={() => { window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`, '_blank'); onClose(); }} className="w-full flex items-center gap-3 bg-gray-50 hover:bg-gray-100 active:scale-95 transition-all rounded-2xl px-4 py-4">
          <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center shrink-0 text-lg">🗺️</div>
          <div className="text-left"><div className="text-sm font-semibold text-gray-800">Google Maps</div><div className="text-xs text-gray-400">Abrir en Google Maps</div></div>
        </button>
        <button onClick={() => { window.open(`https://waze.com/ul?ll=${lat},${lng}&navigate=yes`, '_blank'); onClose(); }} className="w-full flex items-center gap-3 bg-gray-50 hover:bg-gray-100 active:scale-95 transition-all rounded-2xl px-4 py-4">
          <div className="w-10 h-10 bg-cyan-50 rounded-xl flex items-center justify-center shrink-0 text-lg">🚗</div>
          <div className="text-left"><div className="text-sm font-semibold text-gray-800">Waze</div><div className="text-xs text-gray-400">Abrir en Waze</div></div>
        </button>
        <button onClick={onClose} className="w-full py-3.5 rounded-2xl text-sm font-semibold text-gray-500 bg-gray-100 mt-1">Cancelar</button>
      </div>
    </div>
  );
}

// ── Pickup confirm panel ───────────────────────────────────
function PickupPanel({ onConfirm, onCancel, loading }) {
  const [note, setNote]               = useState('');
  const [images, setImages]           = useState([]);
  const [compressing, setCompressing] = useState(false);
  const [error, setError]             = useState('');

  const canSubmit = note.trim().length > 0 && images.length > 0 && !compressing && !loading;

  function handleConfirm() {
    setError('');
    if (!note.trim()) { setError('La nota de recojo es requerida'); return; }
    if (images.length === 0) { setError('Agrega al menos una foto del recojo'); return; }
    onConfirm({ note: note.trim(), files: images.map(i => i.file) });
  }

  return (
    <div className="space-y-4 bg-orange-50 rounded-2xl p-4">
      <div className="flex items-center gap-2 text-sm font-bold text-orange-600">
        <Package size={16} />
        Confirmar recojo
      </div>

      <div className="space-y-1">
        <label className="text-xs text-gray-500 font-medium">Nota de recojo <span className="text-red-400">*</span></label>
        <textarea
          rows={3}
          placeholder='ej. "Paquete recogido en recepción" — requerido'
          value={note}
          onChange={e => { setNote(e.target.value); setError(''); }}
          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none bg-white"
          autoFocus
        />
      </div>

      <div className="border-t border-orange-100" />

      <ImagePickerSection images={images} setImages={setImages} compressing={compressing} setCompressing={setCompressing} />

      {error && (
        <div className="bg-red-50 text-red-600 text-xs rounded-xl px-3 py-2.5 flex items-center gap-2">
          <AlertTriangle size={13} className="shrink-0" />{error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 pt-1">
        <button onClick={onCancel} disabled={loading} className="py-3 rounded-xl bg-white border border-gray-200 text-gray-600 text-sm font-semibold disabled:opacity-50">Cancelar</button>
        <button onClick={handleConfirm} disabled={!canSubmit} className="py-3 rounded-xl bg-orange-500 text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
          {loading ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Subiendo...</> : <><Package size={15} />Confirmar recojo</>}
        </button>
      </div>
    </div>
  );
}

// ── Delivery submit panel ──────────────────────────────────
function SubmitPanel({ targetStatus, charge, onConfirm, onCancel, loading }) {
  const [note, setNote]               = useState('');
  const [images, setImages]           = useState([]);
  const [compressing, setCompressing] = useState(false);
  const [payment, setPayment]         = useState({ charged: 0, fastmax: 0, provider: 0, exchange: 0 });
  const [payExpanded, setPayExpanded] = useState(!!charge);
  const [error, setError]             = useState('');

  const isRejection  = targetStatus === 'rechazado';
  const hasCharge    = charge !== null && charge !== undefined && charge !== '';
  const chargeNum    = hasCharge ? parseFloat(charge) : 0;
  const totalEntered = (payment.charged || 0) + (payment.fastmax || 0) + (payment.provider || 0) + (payment.exchange || 0);
  const mismatch     = hasCharge && Math.abs(totalEntered - chargeNum) > 0.01;
  const canSubmit    = note.trim().length > 0 && images.length > 0 && !compressing && !loading;

  const config = {
    entregado: { title: 'Confirmar entrega',  confirmLabel: 'Confirmar entrega',  confirmColor: 'bg-brand-green', Icon: CheckCircle },
    rechazado: { title: 'Reportar rechazo',   confirmLabel: 'Confirmar rechazo',  confirmColor: 'bg-red-500',     Icon: AlertTriangle },
  }[targetStatus];
  const { Icon: ConfirmIcon } = config;

  function handleConfirm() {
    setError('');
    if (!note.trim()) { setError(isRejection ? 'El motivo es requerido' : 'La nota de entrega es requerida'); return; }
    if (images.length === 0) { setError('Agrega al menos una foto como prueba'); return; }
    onConfirm({ note: note.trim(), files: images.map(i => i.file), payment });
  }

  const payFields = [
    { key: 'charged',  label: 'Efectivo',  color: 'text-brand-green' },
    { key: 'fastmax',  label: 'Fast Max',  color: 'text-brand-primary' },
    { key: 'provider', label: 'Proveedor', color: 'text-brand-gold' },
    { key: 'exchange', label: 'Vuelto',    color: 'text-gray-500' },
  ];

  return (
    <div className="space-y-4 bg-gray-50 rounded-2xl p-4">
      <div className={`flex items-center gap-2 text-sm font-bold ${isRejection ? 'text-red-600' : 'text-brand-green'}`}>
        <ConfirmIcon size={16} />{config.title}
      </div>

      {/* Note */}
      <div className="space-y-1">
        <label className="text-xs text-gray-500 font-medium">{isRejection ? 'Motivo del rechazo' : 'Nota de entrega'} <span className="text-red-400">*</span></label>
        <textarea rows={3} placeholder={isRejection ? 'Motivo del rechazo — requerido' : 'Nota de entrega — requerido'} value={note} onChange={e => { setNote(e.target.value); setError(''); }} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 resize-none bg-white" autoFocus />
      </div>

      <div className="border-t border-gray-200" />

      {/* Images */}
      <ImagePickerSection images={images} setImages={setImages} compressing={compressing} setCompressing={setCompressing} />

      <div className="border-t border-gray-200" />

      {/* Payment — collapsible */}
      {!isRejection && (
        <div className="space-y-2">
          <button onClick={() => setPayExpanded(v => !v)} className="w-full flex items-center justify-between text-left">
            <div className="flex items-center gap-2">
              <DollarSign size={14} className="text-brand-gold" />
              <span className="text-xs font-semibold text-gray-600">
                {hasCharge ? `Cobro al destinatario — S/ ${chargeNum.toFixed(2)}` : 'Registrar cobro (opcional)'}
              </span>
            </div>
            <ChevronDown size={14} className={`text-gray-400 transition-transform ${payExpanded ? 'rotate-180' : ''}`} />
          </button>
          {payExpanded && (
            <div className="bg-white rounded-xl border border-gray-200 p-3 space-y-3">
              {mismatch && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <AlertTriangle size={13} className="text-amber-500 shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-700">
                    Ingresado: S/ {totalEntered.toFixed(2)} — Esperado: S/ {chargeNum.toFixed(2)}
                    {' '}({totalEntered < chargeNum ? `Falta S/ ${(chargeNum - totalEntered).toFixed(2)}` : `Exceso S/ ${(totalEntered - chargeNum).toFixed(2)}`})
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                {payFields.map(({ key, label, color }) => (
                  <div key={key} className="space-y-1">
                    <label className={`text-xs font-medium ${color}`}>{label}</label>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">S/</span>
                      <input type="number" min="0" step="0.01" placeholder="0.00" value={payment[key] || ''} onChange={e => setPayment(prev => ({ ...prev, [key]: parseFloat(e.target.value) || 0 }))} className="w-full pl-6 pr-1 py-1.5 text-sm font-semibold text-gray-800 border border-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-primary/30 text-right" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 text-red-600 text-xs rounded-xl px-3 py-2.5 flex items-center gap-2">
          <AlertTriangle size={13} className="shrink-0" />{error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 pt-1">
        <button onClick={onCancel} disabled={loading} className="py-3 rounded-xl bg-white border border-gray-200 text-gray-600 text-sm font-semibold disabled:opacity-50">Cancelar</button>
        <button onClick={handleConfirm} disabled={!canSubmit} className={`py-3 rounded-xl text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2 ${config.confirmColor}`}>
          {loading ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Subiendo...</> : <><ConfirmIcon size={15} />{config.confirmLabel}</>}
        </button>
      </div>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────
export default function DriverExpressDetail({ delivery, user_id, onClose, onUpdate, onOpenMap }) {
  const [loading, setLoading]           = useState(false);
  const [submitTarget, setSubmitTarget] = useState(null); // 'pickup' | 'entregado' | 'rechazado'
  const [error, setError]               = useState('');
  const [navTarget, setNavTarget]       = useState(null); // for address tap nav sheet

  const isTerminal = ['entregado', 'rechazado', 'cancelado'].includes(delivery.status);

  // ── pending → en_recojo (Iniciar ruta) ────────────────────
  async function handleStartRoute() {
    setLoading(true);
    setError('');
    try {
      await axios.patch(
        `${API_URL}/api/driver/express/deliveries/${delivery.id}/status`,
        { user_id, status: 'en_recojo', reason: 'Conductor en camino al punto de recojo' }
      );
      const updated = { ...delivery, status: 'en_recojo' };
      onUpdate(updated);
      // Auto-open map focused on pickup point
      onOpenMap({ delivery: updated, focusOnPickup: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Error al iniciar la ruta');
    } finally {
      setLoading(false);
    }
  }

  // ── en_recojo → encamino (Confirmar recojo) ───────────────
  async function handlePickupConfirm({ note, files }) {
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('user_id', user_id);
      formData.append('note', note);
      files.forEach(f => formData.append('images', f));

      await axios.post(
        `${API_URL}/api/driver/express/deliveries/${delivery.id}/pickup`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const updated = { ...delivery, status: 'encamino', pickup_driver_notes: note };
      onUpdate(updated);
      setSubmitTarget(null);
      // Auto-open map focused on dropoff point
      onOpenMap({ delivery: updated, focusOnPickup: false });
    } catch (err) {
      setError(err.response?.data?.error || 'Error al confirmar el recojo');
    } finally {
      setLoading(false);
    }
  }

  // ── encamino → entregado / rechazado ──────────────────────
  async function handleSubmit({ note, files, payment }) {
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('user_id', user_id);
      formData.append('status', submitTarget);
      formData.append('note', note);
      if (payment.charged)  formData.append('charged',                  payment.charged);
      if (payment.fastmax)  formData.append('fastmax_transfer_amount',  payment.fastmax);
      if (payment.provider) formData.append('provider_transfer_amount', payment.provider);
      if (payment.exchange) formData.append('fastmax_exchange_amount',  payment.exchange);
      files.forEach(f => formData.append('images', f));

      await axios.post(
        `${API_URL}/api/driver/express/deliveries/${delivery.id}/submit`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      onUpdate({ ...delivery, status: submitTarget });
      setSubmitTarget(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al enviar la entrega');
    } finally {
      setLoading(false);
    }
  }

  function openWhatsApp() {
    const text = encodeURIComponent(
      `¡Hola ${delivery.recipient_name}! 👋\n` +
      `Soy tu repartidor de *Fast Max*.\n` +
      `Estoy en camino a entregarte tu pedido en:\n` +
      `📍 ${delivery.dropoff_address}\n\n` +
      `¿Estás disponible para recibirlo?`
    );
    window.open(`https://wa.me/${delivery.recipient_phone.replace(/\D/g, '')}?text=${text}`, '_blank');
  }

  function openWhatsAppSender() {
    if (!delivery.sender?.phone_number) return;
    const text = encodeURIComponent(
      `¡Hola ${delivery.sender.username}! 👋\n` +
      `Soy tu repartidor de *Fast Max*.\n` +
      `Te contacto sobre tu entrega #${delivery.id}:\n` +
      `📦 *Recojo:* ${delivery.pickup_address}\n` +
      `📍 *Entrega:* ${delivery.dropoff_address}\n\n` +
      `¿Tienes alguna indicación adicional?`
    );
    window.open(`https://wa.me/${delivery.sender.phone_number.replace(/\D/g, '')}?text=${text}`, '_blank');
  }

  const cfg = STATUS_CONFIG[delivery.status] || STATUS_CONFIG.pending;
  const showContactButtons = ACTIVE_STATUSES.includes(delivery.status);

  return (
    <>
      {/* Navigation app picker for address taps */}
      {navTarget && (
        <AddressNavSheet
          lat={navTarget.lat}
          lng={navTarget.lng}
          label={navTarget.label}
          onClose={() => setNavTarget(null)}
        />
      )}


      <div className="fixed inset-0 bg-black/60 z-50 flex flex-col justify-end">
        <div className="bg-white rounded-t-3xl max-h-[92vh] flex flex-col">

          {/* Handle */}
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 bg-gray-200 rounded-full" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.color}`}>{cfg.label}</span>
              <span className="text-xs text-gray-400 font-mono">#{delivery.id}</span>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-xl transition-colors">
              <X size={18} className="text-gray-500" />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5">

            {/* Earnings */}
            <div className="bg-brand-green/10 rounded-2xl p-4 text-center">
              <div className="text-xs text-gray-500 mb-1">Tu ganancia por esta entrega</div>
              <div className="text-3xl font-bold text-brand-green">S/ {Number(delivery.driver_earnings).toFixed(2)}</div>
              <div className="text-xs text-gray-400 mt-1">{delivery.distance_km} km</div>
              {delivery.charge && (
                <div className="mt-2 text-xs text-brand-gold font-semibold">
                  Cobrar al destinatario: S/ {Number(delivery.charge).toFixed(2)}
                </div>
              )}
            </div>

            {/* Route — addresses are tappable */}
            <div className="space-y-2">
              <button
                onClick={() => setNavTarget({ lat: delivery.pickup_lat, lng: delivery.pickup_lng, label: 'Punto de recojo' })}
                className="w-full flex items-start gap-3 active:bg-gray-50 rounded-xl p-2 -mx-2 transition-colors"
              >
                <div className="mt-0.5 w-7 h-7 rounded-full bg-brand-green/15 flex items-center justify-center shrink-0">
                  <MapPin size={13} className="text-brand-green" />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-xs text-gray-400 font-medium flex items-center gap-1">
                    Recojo <Navigation size={10} className="text-gray-300" />
                  </div>
                  <div className="text-sm text-gray-800">{delivery.pickup_address}</div>
                </div>
              </button>

              <div className="ml-3.5 w-px h-4 bg-gray-200" />

              <button
                onClick={() => setNavTarget({ lat: delivery.dropoff_lat, lng: delivery.dropoff_lng, label: 'Punto de entrega' })}
                className="w-full flex items-start gap-3 active:bg-gray-50 rounded-xl p-2 -mx-2 transition-colors"
              >
                <div className="mt-0.5 w-7 h-7 rounded-full bg-brand-primary/15 flex items-center justify-center shrink-0">
                  <MapPin size={13} className="text-brand-primary" />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-xs text-gray-400 font-medium flex items-center gap-1">
                    Entrega <Navigation size={10} className="text-gray-300" />
                  </div>
                  <div className="text-sm text-gray-800">{delivery.dropoff_address}</div>
                </div>
              </button>
            </div>

            {/* Map button */}
            {!isTerminal && (
              <button
                onClick={() => {
                  onOpenMap({ delivery, focusOnPickup: delivery.status === 'pending' || delivery.status === 'en_recojo' });
                }}
                className="w-full flex items-center justify-between bg-gray-50 hover:bg-gray-100 rounded-xl px-4 py-3 transition-colors active:scale-95"
              >
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <Map size={16} className="text-brand-primary" />
                  Ver ruta en mapa
                </div>
                <ChevronRight size={15} className="text-gray-400" />
              </button>
            )}

            {/* Remitente (pickup contact) — always visible */}
            {delivery.sender && (
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Remitente</div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-gray-800">{delivery.sender.username}</div>
                    <div className="text-sm text-gray-500">{delivery.sender.phone_number}</div>
                  </div>
                  <div className="flex gap-2">
                    <a
                      href={`tel:${delivery.sender.phone_number}`}
                      className="w-10 h-10 bg-brand-primary rounded-full flex items-center justify-center shrink-0"
                    >
                      <Phone size={16} className="text-white" />
                    </a>
                    <button
                      onClick={openWhatsAppSender}
                      className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center shrink-0"
                    >
                      <MessageCircle size={16} className="text-white" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Recipient */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Destinatario</div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-gray-800">{delivery.recipient_name}</div>
                  <div className="text-sm text-gray-500">{delivery.recipient_phone}</div>
                </div>
                {showContactButtons && (
                  <div className="flex gap-2">
                    <a href={`tel:${delivery.recipient_phone}`} className="w-10 h-10 bg-brand-green rounded-full flex items-center justify-center">
                      <Phone size={16} className="text-white" />
                    </a>
                    <button onClick={openWhatsApp} className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                      <MessageCircle size={16} className="text-white" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Items */}
            {delivery.items && delivery.items.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                  <Package size={13} />Productos ({delivery.items.length})
                </div>
                <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
                  {delivery.items.map(item => (
                    <div key={item.id} className="flex items-center justify-between px-4 py-3">
                      <div>
                        <div className="text-sm font-medium text-gray-800">{item.product_name}</div>
                        <div className="text-xs text-gray-400 font-mono">{item.product_sku}</div>
                      </div>
                      <div className="text-sm text-gray-600 font-semibold">x{item.quantity}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pickup proof (shown after pickup confirmed) */}
            {delivery.pickup_driver_notes && (
              <div className="bg-orange-50 rounded-xl px-4 py-3 space-y-2">
                <div className="text-xs font-semibold text-orange-600 flex items-center gap-1.5">
                  <Package size={13} />Nota de recojo
                </div>
                <div className="text-sm text-gray-700">{delivery.pickup_driver_notes}</div>
                {delivery.pickup_images && delivery.pickup_images.length > 0 && (
                  <div className="flex gap-2 overflow-x-auto pb-1 pt-1">
                    {delivery.pickup_images.map(img => (
                      <img key={img.id} src={img.image_url} alt="pickup" className="w-20 h-20 object-cover rounded-xl shrink-0 border border-orange-100" />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Delivery proof images */}
            {delivery.delivery_images && delivery.delivery_images.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                  <ImageIcon size={13} />Fotos de entrega
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {delivery.delivery_images.map(img => (
                    <img key={img.id} src={img.image_url} alt="proof" className="w-24 h-24 object-cover rounded-xl shrink-0 border border-gray-100" />
                  ))}
                </div>
              </div>
            )}

            {/* Payment summary */}
            {delivery.charged !== null && delivery.charged !== undefined && (
              <div className="bg-gray-50 rounded-xl px-4 py-3 space-y-1.5">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                  <DollarSign size={13} />Cobro registrado
                </div>
                {[
                  { label: 'Efectivo',  val: delivery.charged },
                  { label: 'Fast Max',  val: delivery.fastmax_transfer_amount },
                  { label: 'Proveedor', val: delivery.provider_transfer_amount },
                  { label: 'Vuelto',    val: delivery.fastmax_exchange_amount },
                ].filter(r => r.val).map(r => (
                  <div key={r.label} className="flex justify-between text-xs">
                    <span className="text-gray-500">{r.label}</span>
                    <span className="font-semibold text-gray-700">S/ {Number(r.val).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Notes */}
            {delivery.notes && (
              <div className="bg-brand-gold/10 rounded-xl px-4 py-3">
                <div className="text-xs font-semibold text-brand-gold mb-1">Notas</div>
                <div className="text-sm text-gray-700">{delivery.notes}</div>
              </div>
            )}

            {/* Status timeline */}
            {delivery.status_logs && delivery.status_logs.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Historial</div>
                <div className="space-y-2">
                  {delivery.status_logs.map((log, idx) => (
                    <div key={log.id} className="flex gap-3 items-start">
                      <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${idx === delivery.status_logs.length - 1 ? 'bg-brand-primary/15' : 'bg-gray-100'}`}>
                        <Clock size={10} className={idx === delivery.status_logs.length - 1 ? 'text-brand-primary' : 'text-gray-400'} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-gray-700">{STATUS_LABELS_LOG[log.to_status] || log.to_status}</div>
                        {log.reason && <div className="text-xs text-gray-400">{log.reason}</div>}
                        <div className="text-xs text-gray-400">{formatDateTime(log.created_at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Global error */}
            {error && (
              <div className="bg-red-50 text-red-600 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
                <AlertTriangle size={14} className="shrink-0" />{error}
              </div>
            )}
          </div>

          {/* ── Sticky action area ── */}
          {!isTerminal && (
            <div className="px-5 py-4 border-t border-gray-100 space-y-2 bg-white">

              {/* pending → en_recojo */}
              {delivery.status === 'pending' && !submitTarget && (
                <button
                  onClick={handleStartRoute}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-brand-primary text-white py-4 rounded-2xl text-base font-bold active:scale-95 transition-transform disabled:opacity-60"
                >
                  <Truck size={18} />
                  {loading ? 'Actualizando...' : 'Iniciar ruta'}
                </button>
              )}

              {/* en_recojo → encamino (pickup confirm) */}
              {delivery.status === 'en_recojo' && !submitTarget && (
                <button
                  onClick={() => { setSubmitTarget('pickup'); setError(''); }}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-orange-500 text-white py-4 rounded-2xl text-base font-bold active:scale-95 transition-transform disabled:opacity-60"
                >
                  <Package size={18} />
                  Confirmar recojo
                </button>
              )}

              {/* encamino → entregado / rechazado */}
              {delivery.status === 'encamino' && !submitTarget && (
                <div className="space-y-2">
                  <button
                    onClick={() => { setSubmitTarget('entregado'); setError(''); }}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 bg-brand-green text-white py-4 rounded-2xl text-base font-bold active:scale-95 transition-transform disabled:opacity-60"
                  >
                    <CheckCircle size={18} />Confirmar entrega
                  </button>
                  <button
                    onClick={() => { setSubmitTarget('rechazado'); setError(''); }}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 bg-gray-100 text-red-500 py-3 rounded-2xl text-sm font-semibold active:scale-95 transition-transform disabled:opacity-60"
                  >
                    <AlertTriangle size={15} />Reportar rechazo
                  </button>
                </div>
              )}

              {/* Pickup confirm panel */}
              {submitTarget === 'pickup' && (
                <PickupPanel
                  loading={loading}
                  onConfirm={handlePickupConfirm}
                  onCancel={() => { setSubmitTarget(null); setError(''); }}
                />
              )}

              {/* Delivery submit panel */}
              {(submitTarget === 'entregado' || submitTarget === 'rechazado') && (
                <SubmitPanel
                  targetStatus={submitTarget}
                  charge={delivery.charge}
                  loading={loading}
                  onConfirm={handleSubmit}
                  onCancel={() => { setSubmitTarget(null); setError(''); }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}